/**
 * cockpitYtd — PURE Aggregatoren für „Ergebnis YTD (Doppel-Basis)" (E3, M-CFO-3,
 * `architecture-cockpit-cfo.md` §8.3) und „Umsatz-Wachstum MoM/YoY" (E5, §8.5).
 *
 * DISCIPLINE (Muster `utils/cockpitChartData.ts`): kein Firestore-Import, kein
 * React-Import, keine Seiteneffekte — nur Typ-Importe (zur Compile-Zeit erased).
 * Trivial unit-testbar ohne Mocking. Alle Geldbeträge Integer-Cents,
 * vorzeichen-aware (Storno negativ — nie filtern, Vorzeichen mitnehmen,
 * Signed-Sum-Lehre). Alle Datumsvergleiche ISO-8601-String-Vergleiche
 * (lexikografisch korrekt für `YYYY-MM-DD`) bzw. Monats-Index-Arithmetik.
 *
 * Fail-closed-Kernprinzip (§8.3/§11): Die rechnungsbasierte („gestellt")
 * accrual-Reihe ist IMMER vorhanden. Die zahlungsbasierte („vereinnahmt/gezahlt")
 * cash-Reihe wird `null`, sobald die Zahlungsdaten fehlen ODER eine
 * Coverage-Heuristik greift — lieber keine cash-Zahl als eine künstlich
 * niedrige, die wie ein Umsatzeinbruch aussieht.
 *
 * @module utils/cockpitYtd
 */

import type { IncomeEntry } from './expenseSummary';
import type { Expense } from '../types/expenseTypes';
import type { Payment } from '../types/paymentTypes';

// ─── Coverage-Schwelle (D3b, testbare Konstante) ─────────────────────────────

/**
 * Mindest-Deckungsgrad der cash-Reihe (§8.3 Fail-closed-Regel b): deckt die
 * YTD-Zahlungssumme weniger als diesen Bruchteil der rechnungsbasierten
 * YTD-Einnahmen ab (bei vorhandenen Einnahmen), gilt die Zahlungspflege als
 * unvollständig ⇒ `cash: null`, `cashUnavailableReason: 'insufficient_coverage'`.
 * Bewusst konservativ niedrig — lieber die cash-Reihe ausblenden als einen
 * falschen „Einbruch" zeigen. Als Konstante exportiert, um im Test justierbar
 * zu bleiben.
 */
export const MIN_COVERAGE = 0.15;

// ─── Shared date/period helpers (bewusst dupliziert, Muster `cockpitChartData.ts`) ──

/**
 * Validiert einen ISO-Datumsstring streng genug, um NaN/Garbage-Vergleiche
 * downstream zu vermeiden. Ein malformed Wert gilt als „unbekannt" — der
 * Aufrufer MUSS den Eintrag ignorieren (nie zu `0`/`NaN` coercen).
 */
function isValidISODate(value: string | null | undefined): value is string {
  if (typeof value !== 'string') return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return false;
  const month = Number(m[2]);
  const day = Number(m[3]);
  return month >= 1 && month <= 12 && day >= 1 && day <= 31;
}

/** Gregorianisches Schaltjahr. */
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * Firestore-`Timestamp`-Strukturform ohne firebase-Import (Compile-Typ erased).
 * `paidAt` kann als ISO-String ODER als Timestamp (`{ toDate }` / `{ seconds }`)
 * vorliegen — beide Formen werden defensiv zu `YYYY-MM-DD` normalisiert.
 */
function resolvePaymentDateISO(paidAt: Payment['paidAt']): string | null {
  if (typeof paidAt === 'string') {
    return isValidISODate(paidAt) ? paidAt.slice(0, 10) : null;
  }
  if (paidAt && typeof paidAt === 'object') {
    // { toDate(): Date }
    const withToDate = paidAt as { toDate?: () => Date };
    if (typeof withToDate.toDate === 'function') {
      try {
        const d = withToDate.toDate();
        if (d instanceof Date && !Number.isNaN(d.getTime())) return toISODateUTC(d);
      } catch {
        return null;
      }
    }
    // { seconds: number }
    const withSeconds = paidAt as { seconds?: number };
    if (typeof withSeconds.seconds === 'number' && Number.isFinite(withSeconds.seconds)) {
      const d = new Date(withSeconds.seconds * 1000);
      if (!Number.isNaN(d.getTime())) return toISODateUTC(d);
    }
  }
  return null;
}

/** `Date` → `YYYY-MM-DD` in UTC (kein DST-/Zeitzonen-Drift). */
function toISODateUTC(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Inklusiv-Fenster-Test auf ISO-`YYYY-MM-DD`-Strings (lexikografisch). */
function inWindow(dateISO: string, startISO: string, endISO: string): boolean {
  return dateISO >= startISO && dateISO <= endISO;
}

/**
 * Σ der Einnahmen (brutto, vorzeichen-aware) mit validem `dateISO` im
 * inklusiven Fenster `[startISO, endISO]`. Malformed Daten werden ignoriert.
 */
function sumIncomeInWindow(income: IncomeEntry[] | null | undefined, startISO: string, endISO: string): number {
  let sum = 0;
  for (const entry of income ?? []) {
    if (!entry || !isValidISODate(entry.dateISO)) continue;
    if (inWindow(entry.dateISO.slice(0, 10), startISO, endISO)) sum += entry.grossCents;
  }
  return sum;
}

/**
 * Σ der gebuchten Ausgaben (brutto, vorzeichen-aware) nach ihrem
 * Perioden-Datum (`dateSelector`) im inklusiven Fenster. Nur `status==='booked'`
 * zählt (Drafts sind Vorschau); Korrektur-Docs zählen mit gespeichertem
 * Vorzeichen (nicht filtern). Malformed Daten werden ignoriert.
 */
function sumBookedExpensesInWindow(
  expenses: Expense[] | null | undefined,
  startISO: string,
  endISO: string,
  dateSelector: (e: Expense) => string,
): number {
  let sum = 0;
  for (const expense of expenses ?? []) {
    if (!expense || expense.status !== 'booked') continue;
    const dateISO = dateSelector(expense);
    if (!isValidISODate(dateISO)) continue;
    if (inWindow(dateISO.slice(0, 10), startISO, endISO)) sum += expense.grossCents;
  }
  return sum;
}

/** Anzahl der in ein Fenster fallenden accrual-Datensätze (für „Vorjahr leer"-Entscheid). */
function countAccrualInWindow(
  income: IncomeEntry[] | null | undefined,
  expenses: Expense[] | null | undefined,
  startISO: string,
  endISO: string,
  expensesEnabled: boolean,
): number {
  let count = 0;
  for (const entry of income ?? []) {
    if (!entry || !isValidISODate(entry.dateISO)) continue;
    if (inWindow(entry.dateISO.slice(0, 10), startISO, endISO)) count += 1;
  }
  if (expensesEnabled) {
    for (const expense of expenses ?? []) {
      if (!expense || expense.status !== 'booked' || !isValidISODate(expense.expenseDate)) continue;
      if (inWindow(expense.expenseDate.slice(0, 10), startISO, endISO)) count += 1;
    }
  }
  return count;
}

// ─── E3 — Ergebnis YTD, Doppel-Basis (§8.3) ──────────────────────────────────

/** Basis einer YTD-Reihe: `accrual` = gestellt (rechnungsbasiert), `cash` = vereinnahmt/gezahlt. */
export type YtdBasis = 'accrual' | 'cash';

/** Eine YTD-Reihe. ALLE Beträge Integer-Cents, vorzeichen-aware. */
export interface YtdSeries {
  /** Σ Einnahmen im YTD-Fenster. */
  incomeYtdCents: number;
  /** Σ Ausgaben im YTD-Fenster (0 wenn `expensesEnabled===false`). */
  expenseYtdCents: number;
  /** `incomeYtdCents − expenseYtdCents`. */
  netYtdCents: number;
}

/** Grund, warum die cash-Reihe fehlt (fail-closed). */
export type CashUnavailableReason = 'no_payments' | 'insufficient_coverage';

/**
 * Ergebnis von {@link computeYtdDual}. Die accrual-Reihe ist IMMER vorhanden;
 * die cash-Reihe ist `null` (mit Grund), wenn Zahlungsdaten fehlen/unplausibel
 * sind. YoY (`yoyDeltaPct`) wird nur auf der robusten accrual-Reihe gebildet.
 */
export interface YtdDualResult {
  /** IMMER vorhanden (rechnungsbasiert, „gestellt"). */
  accrual: YtdSeries;
  /** `null` ⇒ fail-closed (Zahlungsdaten unbrauchbar) → NUR accrual zeigen. */
  cash: YtdSeries | null;
  /** Grund für `cash: null` (sonst `null`). */
  cashUnavailableReason: CashUnavailableReason | null;
  /** Netto der accrual-Reihe des Vorjahres bis zum gleichen Stichtag; `null` wenn Vorjahr leer. */
  prevAccrualNetYtdCents: number | null;
  /** YoY-Delta in Prozent (gerundet) auf accrual-Netto; `null` wenn Basis fehlt/≤0. */
  yoyDeltaPct: number | null;
}

/** Eingabe für {@link computeYtdDual}. */
export interface ComputeYtdDualInput {
  /** accrual-Einnahmen (rechnungsdatiert, brutto) — gebubbelt aus dem Umsatz-Loader. */
  income: IncomeEntry[];
  /** accrual-Ausgaben (gebucht, nach `expenseDate`) — gebubbelt aus dem Ausgaben-Loader. */
  expenses: Expense[];
  /** cash-Einnahmen (`payments`); `null` ⇒ nicht geladen/Fehler ⇒ `cash: null`. */
  payments: Payment[] | null;
  /** cash-Ausgabenbasis; Default = `expenses` nach `expenseDate` (dokumentierte Ungenauigkeit §8.3). */
  paidExpenses?: Expense[];
  /** Referenzdatum `YYYY-MM-DD` (i. d. R. heute) — Parameter für Pure-Testbarkeit. */
  todayISO: string;
  /** `EXPENSES_ENABLED` — `false` ⇒ Ausgaben zählen nicht (Kachel degradiert zu „Einnahmen YTD"). */
  expensesEnabled: boolean;
}

/**
 * Leeres/Nullwertiges Ergebnis (defensiver Rückfall bei ungültigem `todayISO`).
 * accrual bleibt ein valides Nullobjekt (die Reihe ist per Vertrag immer da).
 */
function emptyYtdResult(): YtdDualResult {
  return {
    accrual: { incomeYtdCents: 0, expenseYtdCents: 0, netYtdCents: 0 },
    cash: null,
    cashUnavailableReason: 'no_payments',
    prevAccrualNetYtdCents: null,
    yoyDeltaPct: null,
  };
}

/**
 * Berechnet die Doppel-Basis-YTD-Sicht (§8.3, Entscheidung D3): rechnungsbasiert
 * („gestellt") UND zahlungsbasiert („vereinnahmt/gezahlt") in EINEM Aufruf,
 * plus Vorjahresvergleich (YoY) bis zum gleichen Stichtag auf der accrual-Reihe.
 *
 * accrual-Fenster: `01.01.{Jahr}` … `todayISO` (inklusiv). Einnahmen
 * rechnungsdatiert, Ausgaben `booked` nach `expenseDate`.
 *
 * cash-Reihe: Einnahmen = Σ `payments.amountCents` mit `paidAt` im YTD-Fenster
 * (Storno negativ), Ausgaben = `booked` nach Zahlungsdatum (Fallback
 * `expenseDate`). Wird `null`, wenn `payments === null` (⇒ `no_payments`) ODER
 * die Coverage-Heuristik greift (⇒ `insufficient_coverage`).
 *
 * @param input - {@link ComputeYtdDualInput}
 * @returns {@link YtdDualResult} — accrual immer, cash fail-closed.
 */
export function computeYtdDual(input: ComputeYtdDualInput): YtdDualResult {
  const { income, expenses, payments, paidExpenses, todayISO, expensesEnabled } = input;

  if (!isValidISODate(todayISO)) return emptyYtdResult();

  const year = Number(todayISO.slice(0, 4));
  const startISO = `${year}-01-01`;
  const endISO = todayISO.slice(0, 10);

  // ── accrual-Reihe (immer) ──────────────────────────────────────────────
  const accrualIncome = sumIncomeInWindow(income, startISO, endISO);
  const accrualExpense = expensesEnabled
    ? sumBookedExpensesInWindow(expenses, startISO, endISO, (e) => e.expenseDate)
    : 0;
  const accrual: YtdSeries = {
    incomeYtdCents: accrualIncome,
    expenseYtdCents: accrualExpense,
    netYtdCents: accrualIncome - accrualExpense,
  };

  // ── cash-Reihe (fail-closed) ───────────────────────────────────────────
  let cash: YtdSeries | null = null;
  let cashUnavailableReason: CashUnavailableReason | null = null;

  if (payments === null) {
    cashUnavailableReason = 'no_payments';
  } else {
    let cashIncome = 0;
    for (const payment of payments) {
      if (!payment) continue;
      const paidISO = resolvePaymentDateISO(payment.paidAt);
      if (paidISO === null) continue;
      if (inWindow(paidISO, startISO, endISO)) cashIncome += payment.amountCents;
    }

    // Coverage-Heuristik (§8.3 b): bei vorhandenen accrual-Einnahmen muss die
    // Zahlungssumme einen plausiblen Bruchteil abdecken — sonst ausblenden.
    if (accrualIncome > 0 && cashIncome < accrualIncome * MIN_COVERAGE) {
      cashUnavailableReason = 'insufficient_coverage';
    } else {
      const cashExpense = expensesEnabled
        ? sumBookedExpensesInWindow(paidExpenses ?? expenses, startISO, endISO, (e) => e.expenseDate)
        : 0;
      cash = {
        incomeYtdCents: cashIncome,
        expenseYtdCents: cashExpense,
        netYtdCents: cashIncome - cashExpense,
      };
    }
  }

  // ── YoY auf accrual (gleicher Stichtag Vorjahr) ────────────────────────
  const prevYear = year - 1;
  const prevStartISO = `${prevYear}-01-01`;
  let prevSuffix = todayISO.slice(5, 10); // "MM-DD"
  // Schaltjahr-Clamp: 29.02. im Vorjahr ohne 29. ⇒ auf 28.02. klemmen.
  if (prevSuffix === '02-29' && !isLeapYear(prevYear)) prevSuffix = '02-28';
  const prevEndISO = `${prevYear}-${prevSuffix}`;

  const prevCount = countAccrualInWindow(income, expenses, prevStartISO, prevEndISO, expensesEnabled);
  let prevAccrualNetYtdCents: number | null = null;
  let yoyDeltaPct: number | null = null;
  if (prevCount > 0) {
    const prevIncome = sumIncomeInWindow(income, prevStartISO, prevEndISO);
    const prevExpense = expensesEnabled
      ? sumBookedExpensesInWindow(expenses, prevStartISO, prevEndISO, (e) => e.expenseDate)
      : 0;
    prevAccrualNetYtdCents = prevIncome - prevExpense;
    if (prevAccrualNetYtdCents > 0) {
      yoyDeltaPct = Math.round(((accrual.netYtdCents - prevAccrualNetYtdCents) / prevAccrualNetYtdCents) * 100);
    }
  }

  return { accrual, cash, cashUnavailableReason, prevAccrualNetYtdCents, yoyDeltaPct };
}

// ─── E5 — Umsatz-Wachstum MoM/YoY (§8.5) ─────────────────────────────────────

/** Ergebnis von {@link computeRevenueGrowth}. */
export interface RevenueGrowth {
  /** Basis-Monat = LETZTER ABGESCHLOSSENER Monat (nicht der laufende!), `'YYYY-MM'`. */
  refYearMonth: string;
  /** Σ Umsatz (brutto) im Basis-Monat, Integer-Cents. */
  refMonthCents: number;
  /** YoY-Wachstum ggü. Vorjahres-Basis-Monat in Prozent; `null` wenn Basis ≤ 0 / ohne Einträge. */
  yoyPct: number | null;
  /** MoM-Wachstum ggü. Vormonat in Prozent; `null` wenn Basis ≤ 0 / ohne Einträge. */
  momPct: number | null;
}

/** Baut `'YYYY-MM'` aus einem absoluten Monats-Index (Jahr×12 + (Monat−1)). */
function yearMonthFromIndex(monthIndex: number): string {
  const y = Math.floor(monthIndex / 12);
  const m = ((monthIndex % 12) + 12) % 12; // defensiv gegen negatives Modulo
  return `${y}-${String(m + 1).padStart(2, '0')}`;
}

/**
 * Berechnet Umsatz-Wachstum MoM/YoY auf Basis des LETZTEN VOLLEN Monats (§8.5).
 *
 * Der laufende Monat wird bewusst ausgelassen — ein Vergleich „11 Tage Juli vs.
 * voller Juli Vorjahr" wäre systematisch negativ verzerrt (genau die
 * irreführende Zahl, die wir ausschließen). YoY und MoM sind `null`, wenn die
 * jeweilige Vergleichsbasis ≤ 0 ist (kein Eintrag, 0-Summe oder Storno-negativ
 * ⇒ kein +∞/irreführender Wert).
 *
 * @param income - gebubbelte `IncomeEntry[]` (rechnungsdatiert, brutto).
 * @param todayISO - Referenzdatum `YYYY-MM-DD`.
 * @returns {@link RevenueGrowth}
 */
export function computeRevenueGrowth(income: IncomeEntry[], todayISO: string): RevenueGrowth {
  if (!isValidISODate(todayISO)) {
    return { refYearMonth: '', refMonthCents: 0, yoyPct: null, momPct: null };
  }

  const year = Number(todayISO.slice(0, 4));
  const month = Number(todayISO.slice(5, 7)); // 1..12
  const currentMonthIndex = year * 12 + (month - 1);
  const refMonthIndex = currentMonthIndex - 1; // letzter voller Monat
  const prevMonthIndex = refMonthIndex - 1; // MoM-Basis
  const yoyMonthIndex = refMonthIndex - 12; // YoY-Basis

  // Umsatz je `'YYYY-MM'`-Bucket (vorzeichen-aware), nur relevante Monate.
  const refYearMonth = yearMonthFromIndex(refMonthIndex);
  const prevYearMonth = yearMonthFromIndex(prevMonthIndex);
  const yoyYearMonth = yearMonthFromIndex(yoyMonthIndex);

  const sums = new Map<string, number>([
    [refYearMonth, 0],
    [prevYearMonth, 0],
    [yoyYearMonth, 0],
  ]);

  for (const entry of income ?? []) {
    if (!entry || !isValidISODate(entry.dateISO)) continue;
    const ym = entry.dateISO.slice(0, 7);
    if (sums.has(ym)) sums.set(ym, sums.get(ym)! + entry.grossCents);
  }

  const refMonthCents = sums.get(refYearMonth)!;
  const prevMonthCents = sums.get(prevYearMonth)!;
  const yoyMonthCents = sums.get(yoyYearMonth)!;

  const pctOrNull = (base: number): number | null =>
    base > 0 ? Math.round(((refMonthCents - base) / base) * 100) : null;

  return {
    refYearMonth,
    refMonthCents,
    yoyPct: pctOrNull(yoyMonthCents),
    momPct: pctOrNull(prevMonthCents),
  };
}
